import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../config/apiConfig';

const AuthContext = createContext();

// The packaged desktop app's renderer loads over `file://`, which Chromium
// treats as cross-site relative to `http://localhost:5010` — a SameSite
// cookie (the "Remember me" refresh token) silently never gets sent back on
// that specific build, even though it works fine in the CRA dev server (same
// registrable "localhost" site) and on the web build (real HTTPS origin).
// `window.posDesktop` is only ever injected by desktop/preload.js, so this
// is a reliable way to detect that one broken case and route around it via
// an explicit header instead, without touching the web build's cookie flow.
const DESKTOP_REFRESH_TOKEN_KEY = 'overpos_desktop_refresh_token';
const isDesktopApp = () => typeof window !== 'undefined' && window.posDesktop?.isDesktop === true;

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const decodeToken = useCallback((token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            return JSON.parse(jsonPayload);
        } catch (e) {
            console.error("Token decoding failed", e);
            return null;
        }
    }, []);

    const logout = useCallback((reason) => {
        if (reason) {
            sessionStorage.setItem('logout_reason', reason);
        }
        localStorage.clear();
        sessionStorage.removeItem('user'); // Keep others if needed, but clear user
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    const login = useCallback((token, userData, refreshToken) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));

        // Only ever present when this is the desktop app talking to a
        // desktop-local backend (see authController.js) — the web build
        // never receives this field and this is simply a no-op for it.
        if (isDesktopApp() && refreshToken) {
            localStorage.setItem(DESKTOP_REFRESH_TOKEN_KEY, refreshToken);
        }

        const decoded = decodeToken(token);
        // Prioritize decoded privileges from JWT for security/speed
        setUser({ ...userData, privileges: decoded?.privileges || userData.privileges });
        setIsAuthenticated(true);
    }, [decodeToken]);

    // "Remember me" — super admin only. Silently exchanges the refresh
    // token for a fresh access token, via the same login() call a real login
    // uses. Returns false (without throwing or showing anything to the user)
    // if there's no valid remembered session — that's the normal case for
    // everyone else. Tries the httpOnly cookie first (the ordinary path for
    // the web build and the CRA dev server); the desktop app's packaged
    // build additionally sends its token via header, since the cookie never
    // survives the file:// -> http://localhost boundary there.
    const tryRefresh = useCallback(async () => {
        try {
            const desktopToken = isDesktopApp() ? localStorage.getItem(DESKTOP_REFRESH_TOKEN_KEY) : null;
            const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: desktopToken ? { 'x-refresh-token': desktopToken } : undefined,
            });
            if (!response.ok) {
                if (desktopToken) localStorage.removeItem(DESKTOP_REFRESH_TOKEN_KEY);
                return false;
            }

            const data = await response.json().catch(() => ({}));
            if (!data.token || !data.user) return false;

            login(data.token, data.user, data.refresh_token);
            return true;
        } catch (error) {
            console.error('Silent refresh failed:', error);
            return false;
        }
    }, [login]);

    useEffect(() => {
        const bootstrap = async () => {
            const token = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');

            if (token && savedUser) {
                const decoded = decodeToken(token);
                if (decoded) {
                    // Check expiry if possible, or just re-hydrate
                    const now = Date.now() / 1000;
                    if (decoded.exp && decoded.exp < now) {
                        // Local token expired — a "remembered" session may still
                        // be alive via the refresh cookie; try before giving up.
                        if (!(await tryRefresh())) logout();
                    } else {
                        const userData = JSON.parse(savedUser);
                        setUser({ ...userData, privileges: decoded.privileges || userData.privileges });
                        setIsAuthenticated(true);
                    }
                } else {
                    if (!(await tryRefresh())) logout();
                }
            } else {
                // No local session at all — still worth a quiet attempt in case
                // this is a remembered super admin on a fresh tab/browser whose
                // localStorage was cleared but the refresh cookie is still valid.
                await tryRefresh();
            }
            setIsLoading(false);
        };

        bootstrap();
    }, [decodeToken, logout, tryRefresh]);

    const updateUser = useCallback((updatedFields) => {
        setUser(prev => {
            if (!prev) return prev;
            const merged = { ...prev, ...updatedFields };
            localStorage.setItem('user', JSON.stringify(merged));
            return merged;
        });
    }, []);

    // Exposed status check for real-time security enforcement
    const checkStatus = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token || !isAuthenticated) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.logout) {
                    console.log('Force logout triggered:', data.message);
                    logout(data.message);
                }
            } else if (response.status === 401) {
                // authMiddleware itself can reject the request before it ever
                // reaches the /status handler (e.g. auth_version or
                // session_version mismatch) — it still sends a `message`
                // explaining why, so surface it the same way instead of a
                // silent, unexplained logout.
                const data = await response.json().catch(() => ({}));
                logout(data.message);
            }
        } catch (error) {
            console.error('Status check failed:', error);
        }
    }, [isAuthenticated, logout]);

    // Polling effect: Check user status every 30 seconds if authenticated
    useEffect(() => {
        let intervalId;

        if (isAuthenticated) {
            // Check immediately on mount/auth change
            checkStatus();
            // Then set interval
            intervalId = setInterval(checkStatus, 30000); // 30 seconds
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [isAuthenticated, checkStatus]);

    // Helper: Check component access
    const hasPrivilege = useCallback((component) => {
        if (!user) return false;
        if (user.is_super_admin) return true;
        const normalized = component.toLowerCase();
        return user.privileges?.components?.[normalized] === true;
    }, [user]);

    // Helper: Check sub-component access
    const hasSubComponentAccess = useCallback((subId) => {
        if (!user) return false;
        if (user.is_super_admin) return true;
        return user.privileges?.subComponents?.[subId] === true;
    }, [user]);

    // Helper: Check function access
    const hasFunctionAccess = useCallback((func) => {
        if (!user) return false;
        if (user.is_super_admin) return true;
        return user.privileges?.functions?.[func] === true;
    }, [user]);

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated,
            isLoading,
            login,
            logout,
            updateUser,
            hasPrivilege,
            hasSubComponentAccess,
            hasFunctionAccess,
            checkStatus
        }}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
};
